import React, { useEffect, useRef } from "react";
import { bool, func, instanceOf } from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { Col, Row } from "react-bootstrap";
import { Field, Form, Formik } from "formik";
import { DateRangeField } from "pages/_components/fields/DateRangeField";
import Selector from "pages/_components/fields/formik/Selector";
import Button from "pages/_components/Button";
import TextField from "pages/_components/fields/TextField";
import { actions as echeckActions } from "reducers/echecks";
import { ownerships, states } from "util/echecks";
import { getLimitDate } from "util/date";
import * as i18n from "util/i18n";

const FORM_ID = "echecks";

const FilterForm = ({ customValues, closeSidebar, isRecievedForm, isEmittedForm }) => {
    const formRef = useRef(null);
    const dispatch = useDispatch();
    const { echeqFilters } = useSelector((state) => state.echecks);
    const statesOptions = i18n.formatOption(states, "echecks.states");
    const ownershipsOptions = i18n.formatRadioOption(ownerships, "echecks.ownership");

    const initialValues = {
        ownership: "TODOS",
        status: "TODOS",
        number: "",
        emitted: "",
        receiver: "",
        ...customValues,
    };

    useEffect(() => {
        const requestFilters = {
            createdFrom: initialValues.createdRange[0],
            createdTo: initialValues.createdRange[1],
            payedFrom: initialValues.payedRange[0],
            payedTo: initialValues.payedRange[1],
            emitted: initialValues.emitted || "",
            receiver: initialValues.receiver || "",
            ownership: initialValues.ownership,
            status: initialValues.status,
            number: initialValues.number,
            ...echeqFilters,
        };
        dispatch(echeckActions.setEcheqFilters(requestFilters));
    }, []);

    useEffect(() => {
        if (!formRef.current) {
            return;
        }

        const filters = Object.entries(echeqFilters);
        filters.forEach((item) => {
            if (item[0] === "createdFrom") {
                formRef.current.setFieldValue("createdRange[0]", item[1]);
            }
            if (item[0] === "createdTo") {
                formRef.current.setFieldValue("createdRange[1]", item[1]);
            }
            if (item[0] === "payedFrom") {
                formRef.current.setFieldValue("payedRange[0]", item[1]);
            }
            if (item[0] === "payedTo") {
                formRef.current.setFieldValue("payedRange[1]", item[1]);
            }
            formRef.current.setFieldValue(item[0], item[1]);
        });
    }, [echeqFilters]);

    const handleSubmit = (values) => {
        const requestFilters = {
            ...echeqFilters,
            createdFrom: values.createdRange[0],
            createdTo: values.createdRange[1],
            payedFrom: values.payedRange[0],
            payedTo: values.payedRange[1],
            emitted: values.emitted,
            receiver: values.receiver,
            ownership: values.ownership,
            status: values.status,
            number: values.number,
        };
        dispatch(echeckActions.setEcheqFilters(requestFilters));
        dispatch(echeckActions.filterEcheqList(requestFilters));
        closeSidebar((state) => !state);
    };

    const handleReset = (setFieldValue) => {
        setFieldValue("createdRange", [null, null]);
        setFieldValue("payedRange", [null, null]);
        setFieldValue("emitted", "");
        setFieldValue("receiver", "");
        setFieldValue("ownership", "TODOS");
        setFieldValue("status", "TODOS");
        setFieldValue("number", "");
    };

    return (
        <Formik initialValues={initialValues} onSubmit={handleSubmit} ref={formRef}>
            {({ setFieldValue }) => (
                <Form>
                    <Row>
                        <Col xs={12} md={6}>
                            <Field
                                component={DateRangeField}
                                hidePlaceholder
                                idForm={FORM_ID}
                                minDate={getLimitDate(-3650)}
                                maxDate={getLimitDate(3650)}
                                name="createdRange"
                                dateFormat="dd/MM/yyyy"
                                selectsRange
                                showMonthYearDropdown
                                scrollableMonthYearDropdown
                            />
                        </Col>
                        <Col xs={12} md={6}>
                            <Field
                                component={DateRangeField}
                                hidePlaceholder
                                idForm={FORM_ID}
                                minDate={getLimitDate(-3650)}
                                maxDate={getLimitDate(3650)}
                                name="payedRange"
                                dateFormat="dd/MM/yyyy"
                                selectsRange
                                showMonthYearDropdown
                                scrollableMonthYearDropdown
                            />
                        </Col>
                        {isEmittedForm && (
                            <Col xs={12} md={6}>
                                <Field
                                    component={TextField}
                                    placeholderText="CUIT/CUIL/CDI"
                                    idForm={FORM_ID}
                                    name="emitted"
                                    type="text"
                                />
                            </Col>
                        )}
                        {isRecievedForm && (
                            <Col xs={12} md={6}>
                                <Field
                                    component={TextField}
                                    placeholderText="CUIT/CUIL/CDI"
                                    idForm={FORM_ID}
                                    name="receiver"
                                    type="text"
                                />
                            </Col>
                        )}
                        <Col xs={12} md={6}>
                            <span className="data-label"> </span>
                            <span className="flex">
                                <Field
                                    idForm={FORM_ID}
                                    component={Selector}
                                    renderAs="radio"
                                    inLineControl
                                    name="ownership"
                                    options={ownershipsOptions}
                                />
                            </span>
                        </Col>
                        <Col xs={12} md={6}>
                            <Field
                                idForm={FORM_ID}
                                name="status"
                                component={Selector}
                                options={statesOptions}
                                placeholder={i18n.get("accounts.create.choose")}
                            />
                        </Col>
                        <Col xs={12} md={6}>
                            <Field component={TextField} idForm={FORM_ID} name="number" type="text" hidePlaceholder />
                        </Col>
                    </Row>
                    <Row className="form-actions">
                        <Col xs={12} md={6}>
                            <Button
                                className="btn-outline"
                                label="echecks.clean.filters"
                                onClick={() => handleReset(setFieldValue)}
                            />
                        </Col>
                        <Col xs={12} md={6}>
                            <Button type="submit" className="btn-primary" label="echecks.filtering" />
                        </Col>
                    </Row>
                </Form>
            )}
        </Formik>
    );
};

FilterForm.propTypes = {
    customValues: instanceOf(Object).isRequired,
    closeSidebar: func.isRequired,
    isRecievedForm: bool,
    isEmittedForm: bool,
};

FilterForm.defaultProps = {
    isRecievedForm: false,
    isEmittedForm: false,
};

export default FilterForm;
